import { getApp, getApps, initializeApp } from "firebase/app";
import {
  ReCaptchaEnterpriseProvider,
  getToken,
  initializeAppCheck,
} from "firebase/app-check";

// Kotlin/Wasm looks up an external object as a named module export. Keep that
// interop detail in this small facade while using Firebase's modular named
// exports for every SDK operation.
export const FirebaseWebSdk = {
  getOrInitializeApp(options) {
    return getApps().length > 0 ? getApp() : initializeApp(options);
  },
  initializeAppCheck,
  getAppCheckToken: getToken,
  createReCaptchaEnterpriseProvider(siteKey) {
    return new ReCaptchaEnterpriseProvider(siteKey);
  },
  createCallableProtocolRequest(
    projectId,
    functionsRegion,
    functionName,
    requestJson,
    idToken,
    appCheckToken,
  ) {
    const controller = new AbortController();
    const endpoint = `https://${functionsRegion}-${projectId}.cloudfunctions.net/${encodeURIComponent(functionName)}`;
    const data = JSON.parse(requestJson);
    const direct = functionName === "generateHostedMinutesContent";
    let requestBody = JSON.stringify({ data });
    if (direct) {
      const files = data._sourceFiles;
      delete data._sourceFiles;
      if (!Array.isArray(files) || files.length < 2 || files.length > 3) throw new Error("Invalid source bundle");
      const metadata = new TextEncoder().encode(JSON.stringify(data));
      const documents = files.map(file => Uint8Array.from(atob(file), c => c.charCodeAt(0)));
      const total = documents.reduce((sum, file) => sum + file.length, 0);
      if (metadata.length > 256 * 1024 || total > 30000000) throw new Error("Source bundle is too large");
      const size = new Uint8Array(4);
      new DataView(size.buffer).setUint32(0, metadata.length, false);
      requestBody = new Blob([size, metadata, ...documents]);
    }
    const timeout = setTimeout(() => controller.abort(), 250000);
    const promise = Promise.resolve().then(() => fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": direct ? "application/vnd.minutepilot.hosted-minutes-bundle" : "application/json",
        "Authorization": `Bearer ${idToken}`,
        "X-Firebase-AppCheck": appCheckToken,
      },
      body: requestBody,
      signal: controller.signal,
    })).then(async (response) => {
      const responseText = await response.text();
      let body;
      try {
        body = responseText ? JSON.parse(responseText) : undefined;
      } catch {
        throw callableProtocolError(
          "data-loss",
          "Firebase callable returned a non-JSON response.",
          undefined,
        );
      }
      const isObject = body != null && typeof body === "object" && !Array.isArray(body);
      const hasError = isObject && body.error != null && typeof body.error === "object";
      if (!response.ok || hasError) {
        const error = body && typeof body.error === "object" ? body.error : {};
        const status = typeof error.status === "string" ? error.status : statusCodeToCallableCode(response.status);
        const details = error.details !== undefined
          ? error.details
          : typeof error.code === "string" ? { code: error.code } : undefined;
        throw callableProtocolError(
          callableCode(status),
          typeof error.message === "string" ? error.message : "Firebase callable request failed.",
          details,
        );
      }
      if (!isObject) {
        throw callableProtocolError(
          "data-loss",
          "Firebase callable returned a non-object response.",
          undefined,
        );
      }
      if (direct && !Object.prototype.hasOwnProperty.call(body, "result") && !Object.prototype.hasOwnProperty.call(body, "data")) return JSON.stringify({ result: body });
      // `result` is Firebase's documented callable response field. Keep
      // `data` only as a compatibility allowance while older browser bridges
      // roll forward. Kotlin owns the final envelope decoding so this exact
      // protocol boundary is covered by browser tests.
      if (
        !Object.prototype.hasOwnProperty.call(body, "result") &&
        !Object.prototype.hasOwnProperty.call(body, "data")
      ) {
        throw callableProtocolError(
          "data-loss",
          "Firebase callable returned no result envelope.",
          undefined,
        );
      }
      return JSON.stringify(body);
    }).finally(() => clearTimeout(timeout));
    return { promise, cancel: () => controller.abort() };
  },
  callableProtocolRequestPromise(request) {
    return request.promise;
  },
  cancelCallableProtocolRequest(request) {
    request.cancel();
  },
};

function callableProtocolError(code, message, details) {
  const error = new Error(message);
  error.code = code;
  error.details = details;
  return error;
}

function callableCode(status) {
  return String(status)
    .trim()
    .toLowerCase()
    .replace(/_/g, "-");
}

function statusCodeToCallableCode(status) {
  if (status === 400) return "invalid-argument";
  if (status === 401) return "unauthenticated";
  if (status === 403) return "permission-denied";
  if (status === 404) return "not-found";
  if (status === 409) return "aborted";
  if (status === 429) return "resource-exhausted";
  if (status === 499) return "cancelled";
  if (status === 504) return "deadline-exceeded";
  if (status >= 500) return "internal";
  return "unknown";
}
